import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import {
  Search,
  Plus,
  Layers,
  Calendar,
  Image,
  Video,
  FileText,
  Mic,
  MoreVertical,
  ChevronRight,
  Instagram,
  Youtube,
  Facebook
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';

const DeliverablesPage = () => {
  const { api } = useAuth();
  const [deliverables, setDeliverables] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [newDeliverable, setNewDeliverable] = useState({
    project_id: '',
    type: '',
    title: '',
    platforms: [],
    due_date: '',
    caption: '',
  });

  const types = [
    { id: 'Post', icon: Image },
    { id: 'Carousel', icon: Layers },
    { id: 'Reel', icon: Video },
    { id: 'YouTube Video', icon: Youtube },
    { id: 'Podcast Episode', icon: Mic },
    { id: 'Ad Creative', icon: FileText },
  ];

  const platforms = [
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'facebook', label: 'Facebook', icon: Facebook },
    { id: 'youtube', label: 'YouTube', icon: Youtube },
    { id: 'youtube_shorts', label: 'YT Shorts', icon: Youtube },
    { id: 'spotify', label: 'Spotify', icon: Mic },
  ];

  const stages = [
    { id: 'todo', label: 'To Do', color: 'bg-gray-500' },
    { id: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
    { id: 'for_review', label: 'For Review', color: 'bg-yellow-500' },
    { id: 'approved', label: 'Approved', color: 'bg-green-500' },
    { id: 'published', label: 'Published', color: 'bg-purple-500' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [deliverablesRes, projectsRes] = await Promise.all([
        api.get('/deliverables'),
        api.get('/projects'),
      ]);
      setDeliverables(deliverablesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createDeliverable = async (e) => {
    e.preventDefault();
    try {
      await api.post('/deliverables', newDeliverable);
      setCreateOpen(false);
      setNewDeliverable({
        project_id: '',
        type: '',
        title: '',
        platforms: [],
        due_date: '',
        caption: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create deliverable:', error);
    }
  };

  const updateDeliverableStatus = async (deliverableId, newStatus) => {
    try {
      await api.patch(`/deliverables/${deliverableId}`, { status: newStatus });
      setDeliverables(deliverables.map(d => 
        d.id === deliverableId ? { ...d, status: newStatus } : d
      ));
    } catch (error) {
      console.error('Failed to update deliverable:', error);
    }
  };

  const togglePlatform = (platformId) => {
    setNewDeliverable(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'Unknown';
  };

  const getDeliverablesByStage = (stageId) => {
    return deliverables
      .filter(d => d.status === stageId)
      .filter(d => filterProject === 'all' || d.project_id === filterProject)
      .filter(d => 
        d.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
  };

  const getTypeIcon = (type) => {
    const found = types.find(t => t.id === type);
    return found?.icon || FileText;
  };

  const handleDragStart = (e, deliverableId) => {
    e.dataTransfer.setData('deliverableId', deliverableId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    const deliverableId = e.dataTransfer.getData('deliverableId');
    updateDeliverableStatus(deliverableId, stageId);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-syne font-bold text-white">Deliverables</h1>
            <p className="text-fp-textMuted text-sm mt-1">{deliverables.length} total items</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fp-textMuted" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-fp-surface border-white/10 text-white w-48"
                data-testid="deliverables-search"
              />
            </div>
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="bg-fp-surface border-white/10 text-white w-48">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent className="bg-fp-surface border-white/10">
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={() => setCreateOpen(true)}
              className="btn-primary"
              data-testid="create-deliverable-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Deliverable
            </Button>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {stages.map((stage) => (
              <div
                key={stage.id}
                className="kanban-column"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <h3 className="font-semibold text-white">{stage.label}</h3>
                    <span className="text-fp-textMuted text-sm">
                      ({getDeliverablesByStage(stage.id).length})
                    </span>
                  </div>
                </div>

                {/* Column Content */}
                <div className="space-y-3 min-h-[200px]">
                  {getDeliverablesByStage(stage.id).map((deliverable) => {
                    const TypeIcon = getTypeIcon(deliverable.type);
                    return (
                      <motion.div
                        key={deliverable.id}
                        className="kanban-card"
                        draggable
                        onDragStart={(e) => handleDragStart(e, deliverable.id)}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        data-testid={`deliverable-card-${deliverable.id}`}
                      >
                        {/* Type Badge */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="w-4 h-4 text-primary" />
                            <span className="text-xs text-primary">{deliverable.type}</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h4 className="font-semibold text-white mb-2">{deliverable.title}</h4>
                        
                        {/* Project */}
                        <p className="text-fp-textMuted text-xs mb-3">
                          {getProjectName(deliverable.project_id)}
                        </p>

                        {/* Platforms */}
                        {deliverable.platforms?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {deliverable.platforms.map((platform) => (
                              <span
                                key={platform}
                                className="px-2 py-0.5 bg-white/5 text-fp-textMuted text-xs rounded capitalize"
                              >
                                {platform.replace('_', ' ')}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Due Date */}
                        {deliverable.due_date && (
                          <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                            <Calendar className="w-3 h-3 text-fp-textMuted" />
                            <span className="text-fp-textMuted text-xs">
                              {deliverable.due_date}
                            </span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Create Deliverable Modal */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Create Deliverable</DialogTitle>
            </DialogHeader>
            <form onSubmit={createDeliverable} className="space-y-4">
              <div>
                <Label className="text-fp-textMuted">Project</Label>
                <Select
                  value={newDeliverable.project_id}
                  onValueChange={(value) => setNewDeliverable({ ...newDeliverable, project_id: value })}
                >
                  <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-fp-surface border-white/10">
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-fp-textMuted">Type</Label>
                <Select
                  value={newDeliverable.type}
                  onValueChange={(value) => setNewDeliverable({ ...newDeliverable, type: value })}
                >
                  <SelectTrigger className="mt-1 bg-fp-bg border-white/10 text-white">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-fp-surface border-white/10">
                    {types.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-fp-textMuted">Title</Label>
                <Input
                  value={newDeliverable.title}
                  onChange={(e) => setNewDeliverable({ ...newDeliverable, title: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">Platforms</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {platforms.map((platform) => (
                    <label
                      key={platform.id}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-sm cursor-pointer ${
                        newDeliverable.platforms.includes(platform.id)
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10'
                      }`}
                    >
                      <Checkbox
                        checked={newDeliverable.platforms.includes(platform.id)}
                        onCheckedChange={() => togglePlatform(platform.id)}
                      />
                      <span className="text-sm text-white">{platform.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-fp-textMuted">Due Date</Label>
                <Input
                  type="date"
                  value={newDeliverable.due_date}
                  onChange={(e) => setNewDeliverable({ ...newDeliverable, due_date: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-fp-textMuted">Caption</Label>
                <Textarea
                  value={newDeliverable.caption}
                  onChange={(e) => setNewDeliverable({ ...newDeliverable, caption: e.target.value })}
                  className="mt-1 bg-fp-bg border-white/10 text-white"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full btn-primary">
                Create Deliverable
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default DeliverablesPage;
